/**
 * <TalosNodeDrawer> — Engineer-tier read-only Talos diagnostics for a node (#248)
 *                    + Admin-tier service restart + node reboot (#257).
 *
 * Two tabs:
 *   - Services: live `talosctl services` view (state + health), polled every 5s.
 *     Filterable by service id. Each row exposes a Restart button gated at
 *     `<RoleGate role={Role.Admin}>` (#257).
 *   - dmesg:    one-shot kernel/system log tail. Fetched on tab activation and
 *               on manual refresh — not auto-polled. Substring filter.
 *
 * The drawer also exposes a "Reboot Node" button in the header (Admin-gated)
 * that posts to the existing /action route with `action=reboot`, with a
 * hostname-confirm dialog so an accidental click can't take a node down.
 *
 * The drawer itself does not gate access for the read tabs — callers should
 * wrap the trigger (or this component) with `<RoleGate role={Role.Engineer}>`.
 * The BFF (`requireRole(Role.Engineer)` in #216) is the security boundary;
 * this component is a UX convenience. Mutating actions are gated inline at
 * `Role.Admin` here AND at the BFF + deployment-service mirrors (#216 / #257).
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  RefreshCw,
  Search,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  RotateCcw,
  Power,
} from 'lucide-react';
import { clusterApi } from '@/api/client';
import { RoleGate } from '@/components/auth/RoleGate';
// Namespace import + destructure — see SecretsPanel.tsx / RoleGate.tsx for
// rationale (CJS named-export analysis trips on the types package's
// `exports.X = X` pattern under Rollup).
import { Role } from '@jeket-cloud/types/auth';
import type { TalosServiceInfo } from '@/types';

interface Props {
  siteId: string;
  nodeIp: string;
  nodeName: string;
  onClose: () => void;
}

type Tab = 'services' | 'dmesg';

const SERVICES_REFETCH_MS = 5_000;
const DMESG_TAIL_OPTIONS = [200, 500, 1000] as const;

export default function TalosNodeDrawer({ siteId, nodeIp, nodeName, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('services');
  const [rebootOpen, setRebootOpen] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-[90vw] max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-elevated">
          <div>
            <h3 className="text-sm font-medium text-silver-bright">
              Talos diagnostics — <span className="text-gold font-mono">{nodeName}</span>
            </h3>
            <p className="text-xs text-silver-muted mt-0.5 font-mono">{nodeIp}</p>
          </div>
          <div className="flex items-center gap-2">
            <RoleGate role={Role.Admin}>
              <button
                onClick={() => setRebootOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-status-error/10 text-status-error text-xs font-medium hover:bg-status-error/20 transition-colors"
                data-testid="talos-reboot-node"
              >
                <Power size={12} /> Reboot Node
              </button>
            </RoleGate>
            <button
              onClick={onClose}
              aria-label="Close diagnostics"
              className="p-1.5 rounded hover:bg-surface-elevated text-silver-muted hover:text-silver-bright transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-surface-elevated">
          <TabButton active={tab === 'services'} onClick={() => setTab('services')}>
            Services
          </TabButton>
          <TabButton active={tab === 'dmesg'} onClick={() => setTab('dmesg')}>
            dmesg
          </TabButton>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {tab === 'services' ? (
            <ServicesTab siteId={siteId} nodeIp={nodeIp} nodeName={nodeName} />
          ) : (
            <DmesgTab siteId={siteId} nodeIp={nodeIp} />
          )}
        </div>
      </div>

      {rebootOpen && (
        <RebootNodeDialog
          siteId={siteId}
          nodeIp={nodeIp}
          nodeName={nodeName}
          onClose={() => setRebootOpen(false)}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
        active
          ? 'text-gold border-gold'
          : 'text-silver-muted border-transparent hover:text-silver-bright'
      }`}
    >
      {children}
    </button>
  );
}

// ── Services tab ────────────────────────────────────────────────────────

function ServicesTab({
  siteId,
  nodeIp,
  nodeName,
}: {
  siteId: string;
  nodeIp: string;
  nodeName: string;
}) {
  const [filter, setFilter] = useState('');
  // Toast/banner area — most recent restart outcome (success or error). Sits
  // above the table so it doesn't fight the auto-refresh tick. Cleared by
  // navigating tabs / refreshing.
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['talos-services', siteId, nodeIp],
    queryFn: () => clusterApi.getNodeServices(siteId, nodeIp),
    refetchInterval: SERVICES_REFETCH_MS,
  });

  const filtered = useMemo(() => {
    if (!data) return [] as TalosServiceInfo[];
    const q = filter.trim().toLowerCase();
    if (!q) return data;
    return data.filter((s) => s.id.toLowerCase().includes(q));
  }, [data, filter]);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-surface-elevated">
        <div className="relative flex-1 max-w-xs">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-silver-muted" />
          <input
            type="text"
            placeholder="Filter by id"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full bg-surface-deep border border-surface-elevated rounded pl-7 pr-2 py-1 text-xs text-silver-bright focus:border-gold focus:outline-none"
          />
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          aria-label="Refresh services"
          className="p-1.5 rounded hover:bg-surface-elevated text-silver-muted hover:text-silver-bright transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        </button>
        <span className="text-xs text-silver-muted">
          auto-refresh {Math.round(SERVICES_REFETCH_MS / 1000)}s
        </span>
      </div>

      {/* Restart toast */}
      {toast && (
        <div
          data-testid="talos-restart-toast"
          className={`mx-4 mt-2 px-3 py-2 rounded text-xs flex items-center justify-between ${
            toast.kind === 'success'
              ? 'bg-status-healthy/10 text-status-healthy'
              : 'bg-status-error/10 text-status-error'
          }`}
        >
          <span>{toast.msg}</span>
          <button
            onClick={() => setToast(null)}
            aria-label="Dismiss"
            className="opacity-60 hover:opacity-100"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-8 space-y-2 animate-pulse">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-8 bg-surface-elevated/40 rounded" />
            ))}
          </div>
        ) : isError ? (
          <div className="p-8 text-center text-status-error text-sm">
            Failed to load Talos services
            {error instanceof Error && (
              <div className="text-xs text-silver-muted mt-1 font-mono">{error.message}</div>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-silver-muted text-sm">
            {data && data.length > 0
              ? 'No services match the filter'
              : 'No Talos services reported'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-surface">
              <tr className="border-b border-surface-elevated">
                <Th>Service</Th>
                <Th>State</Th>
                <Th>Health</Th>
                <Th>Last restart</Th>
                <Th>Last error</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((svc) => (
                <ServiceRow
                  key={svc.id}
                  svc={svc}
                  siteId={siteId}
                  nodeIp={nodeIp}
                  nodeName={nodeName}
                  onRestartResult={(r) => setToast(r)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">
      {children}
    </th>
  );
}

function ServiceRow({
  svc,
  siteId,
  nodeIp,
  nodeName,
  onRestartResult,
}: {
  svc: TalosServiceInfo;
  siteId: string;
  nodeIp: string;
  nodeName: string;
  onRestartResult: (r: { kind: 'success' | 'error'; msg: string }) => void;
}) {
  return (
    <tr className="border-b border-surface-elevated/50 hover:bg-surface-elevated/30">
      <td className="px-4 py-2 text-xs text-silver-bright font-mono">{svc.id}</td>
      <td className="px-4 py-2 text-xs">
        <StateBadge state={svc.state} />
      </td>
      <td className="px-4 py-2 text-xs">
        <HealthBadge health={svc.health} />
      </td>
      <td className="px-4 py-2 text-xs text-silver-muted font-mono">
        {svc.lastRestart ? formatTimestamp(svc.lastRestart) : '—'}
      </td>
      <td className="px-4 py-2 text-xs text-status-error/80 truncate max-w-xs" title={svc.lastError}>
        {svc.lastError ?? '—'}
      </td>
      <td className="px-4 py-2 text-xs text-right">
        <RoleGate role={Role.Admin}>
          <RestartServiceButton
            siteId={siteId}
            nodeIp={nodeIp}
            nodeName={nodeName}
            serviceId={svc.id}
            onResult={onRestartResult}
          />
        </RoleGate>
      </td>
    </tr>
  );
}

// ── Restart button + confirmation dialog (#257) ─────────────────────────

function RestartServiceButton({
  siteId,
  nodeIp,
  nodeName,
  serviceId,
  onResult,
}: {
  siteId: string;
  nodeIp: string;
  nodeName: string;
  serviceId: string;
  onResult: (r: { kind: 'success' | 'error'; msg: string }) => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => clusterApi.restartTalosService(siteId, nodeIp, serviceId),
    onSuccess: () => {
      setConfirmOpen(false);
      onResult({ kind: 'success', msg: `Restarted ${serviceId} on ${nodeName}` });
      // Bump the services query so the next tick picks up the new state.
      void queryClient.invalidateQueries({ queryKey: ['talos-services', siteId, nodeIp] });
    },
    onError: (err) => {
      setConfirmOpen(false);
      const msg = err instanceof Error ? err.message : String(err);
      onResult({ kind: 'error', msg: `Failed to restart ${serviceId}: ${msg}` });
    },
  });

  return (
    <>
      <button
        onClick={() => setConfirmOpen(true)}
        disabled={mutation.isPending}
        data-testid={`talos-restart-${serviceId}`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-status-warning/10 text-status-warning text-xs hover:bg-status-warning/20 transition-colors disabled:opacity-50"
      >
        <RotateCcw size={11} className={mutation.isPending ? 'animate-spin' : ''} />
        Restart
      </button>

      {confirmOpen && (
        <RestartServiceDialog
          serviceId={serviceId}
          nodeName={nodeName}
          isPending={mutation.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => mutation.mutate()}
        />
      )}
    </>
  );
}

function RestartServiceDialog({
  serviceId,
  nodeName,
  isPending,
  onCancel,
  onConfirm,
}: {
  serviceId: string;
  nodeName: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
      <div
        className="bg-surface border border-surface-elevated rounded-xl w-full max-w-md mx-4 shadow-2xl"
        role="dialog"
        aria-label={`Confirm restart of ${serviceId}`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-status-warning" />
            <h3 className="text-silver-bright font-semibold text-sm">Restart Talos service</h3>
          </div>
          <button
            onClick={onCancel}
            className="text-silver-muted hover:text-silver-bright transition-colors"
            aria-label="Close confirm"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-silver-muted">
            Restart <span className="font-mono text-silver-bright">{serviceId}</span> on{' '}
            <span className="font-mono text-silver-bright">{nodeName}</span>? This will briefly
            interrupt the service.
          </p>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            data-testid={`talos-restart-confirm-${serviceId}`}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-status-warning text-surface-deep hover:bg-status-warning/80 transition-colors disabled:opacity-50"
          >
            {isPending ? 'Restarting…' : 'Restart'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reboot Node dialog (#257) ───────────────────────────────────────────
//
// Same UX shape as the site decommission dialog (#259): the operator must
// type the node hostname before the Confirm button enables. Posts to the
// existing /action route with `action=reboot` so audit + role gates are
// shared with shutdown/reset/upgrade.

function RebootNodeDialog({
  siteId,
  nodeIp,
  nodeName,
  onClose,
}: {
  siteId: string;
  nodeIp: string;
  nodeName: string;
  onClose: () => void;
}) {
  const [confirmInput, setConfirmInput] = useState('');
  const [resultMsg, setResultMsg] = useState<{ kind: 'success' | 'error'; msg: string } | null>(
    null,
  );

  const mutation = useMutation({
    mutationFn: () => clusterApi.rebootNode(siteId, nodeIp),
    onSuccess: () => {
      setResultMsg({ kind: 'success', msg: `Reboot queued for ${nodeName}` });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      setResultMsg({ kind: 'error', msg: `Failed to reboot ${nodeName}: ${msg}` });
    },
  });

  const canConfirm = confirmInput === nodeName && !mutation.isPending;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
      <div
        className="bg-surface border border-surface-elevated rounded-xl w-full max-w-md mx-4 shadow-2xl"
        role="dialog"
        aria-label={`Confirm reboot of ${nodeName}`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-status-error" />
            <h3 className="text-silver-bright font-semibold text-sm">Reboot Node</h3>
          </div>
          <button
            onClick={onClose}
            className="text-silver-muted hover:text-silver-bright transition-colors"
            aria-label="Close reboot dialog"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-silver-muted">
            Rebooting <span className="font-mono text-silver-bright">{nodeName}</span> will
            interrupt every workload running on it. Type the hostname to confirm.
          </p>
          <div className="bg-surface-deep/50 rounded-lg px-4 py-3">
            <div className="text-xs text-silver-muted">Target Node</div>
            <div className="text-sm text-silver-bright font-mono">{nodeName}</div>
            <div className="text-xs text-silver-muted font-mono mt-0.5">{nodeIp}</div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-silver-muted" htmlFor="reboot-hostname-confirm">
              Type the node hostname to confirm
            </label>
            <input
              id="reboot-hostname-confirm"
              type="text"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={nodeName}
              data-testid="talos-reboot-hostname-input"
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder:text-silver-muted/30 focus:border-gold focus:outline-none"
            />
          </div>
          {resultMsg && (
            <div
              data-testid="talos-reboot-result"
              className={`text-xs rounded px-3 py-2 ${
                resultMsg.kind === 'success'
                  ? 'bg-status-healthy/10 text-status-healthy'
                  : 'bg-status-error/10 text-status-error'
              }`}
            >
              {resultMsg.msg}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
          >
            {resultMsg?.kind === 'success' ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!canConfirm || resultMsg?.kind === 'success'}
            data-testid="talos-reboot-confirm"
            className="px-4 py-2 text-sm font-medium rounded-lg bg-status-error text-white hover:bg-status-error/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? 'Rebooting…' : 'Reboot'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StateBadge({ state }: { state: string }) {
  const ok = state === 'Running';
  const cls = ok
    ? 'bg-status-healthy/10 text-status-healthy'
    : state === 'Stopped'
      ? 'bg-silver-muted/10 text-silver-muted'
      : 'bg-status-warning/10 text-status-warning';
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{state || '—'}</span>;
}

function HealthBadge({ health }: { health: string }) {
  if (health === 'Healthy') {
    return (
      <span className="inline-flex items-center gap-1 text-status-healthy">
        <CheckCircle2 size={12} /> Healthy
      </span>
    );
  }
  if (health === 'Unhealthy') {
    return (
      <span className="inline-flex items-center gap-1 text-status-error">
        <AlertTriangle size={12} /> Unhealthy
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-silver-muted">
      <HelpCircle size={12} /> {health || 'Unknown'}
    </span>
  );
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

// ── dmesg tab ───────────────────────────────────────────────────────────

function DmesgTab({ siteId, nodeIp }: { siteId: string; nodeIp: string }) {
  const [tail, setTail] = useState<number>(200);
  const [filter, setFilter] = useState('');

  // Activated implicitly by mount: useQuery fires on first render. dmesg is
  // never auto-polled — only manual refresh + tail-size change.
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['talos-dmesg', siteId, nodeIp, tail],
    queryFn: () => clusterApi.getNodeDmesg(siteId, nodeIp, tail),
    refetchOnWindowFocus: false,
  });

  const filteredLines = useMemo(() => {
    if (!data) return [] as string[];
    const q = filter.trim().toLowerCase();
    if (!q) return data.lines;
    return data.lines.filter((l) => l.toLowerCase().includes(q));
  }, [data, filter]);

  // Reset filter when node changes (effect runs once per node prop change).
  useEffect(() => {
    setFilter('');
  }, [siteId, nodeIp]);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-surface-elevated">
        <div className="relative flex-1 max-w-xs">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-silver-muted" />
          <input
            type="text"
            placeholder="Filter (substring)"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full bg-surface-deep border border-surface-elevated rounded pl-7 pr-2 py-1 text-xs text-silver-bright focus:border-gold focus:outline-none"
          />
        </div>
        <select
          value={tail}
          onChange={(e) => setTail(parseInt(e.target.value, 10))}
          className="bg-surface-deep border border-surface-elevated rounded px-2 py-1 text-xs text-silver-bright focus:border-gold focus:outline-none"
        >
          {DMESG_TAIL_OPTIONS.map((n) => (
            <option key={n} value={n}>
              tail {n}
            </option>
          ))}
        </select>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          aria-label="Refresh dmesg"
          className="p-1.5 rounded hover:bg-surface-elevated text-silver-muted hover:text-silver-bright transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto bg-surface-deep">
        {isLoading ? (
          <div className="p-4 text-xs text-silver-muted">Loading…</div>
        ) : isError ? (
          <div className="p-8 text-center text-status-error text-sm">
            Failed to load dmesg
            {error instanceof Error && (
              <div className="text-xs text-silver-muted mt-1 font-mono">{error.message}</div>
            )}
          </div>
        ) : filteredLines.length === 0 ? (
          <div className="p-8 text-center text-silver-muted text-sm">
            {data && data.lines.length > 0
              ? 'No lines match the filter'
              : 'No dmesg output'}
          </div>
        ) : (
          <pre className="p-3 text-xs font-mono text-silver-bright whitespace-pre-wrap break-all">
            {filteredLines.join('\n')}
          </pre>
        )}
      </div>
    </div>
  );
}
